<script setup lang="ts">
import { computed } from "vue";
import { RouterLink, useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useAuthStore } from "@/stores/auth";
import { useDashboardAppStore } from "@/stores/dashboardApp";
import constants from "@/constant";
import {
  search,
  code,
  image,
  frame,
  list,
  globe,
  envelope,
  contacts,
  pn_document,
  sliders,
} from "pn-design-assets/pn-assets/icons";

const auth = useAuthStore();
const { role } = storeToRefs(auth);
const dashboardApp = useDashboardAppStore();
const { selectedApp } = storeToRefs(dashboardApp);

const isActive = (name: string) => {
  const route = useRoute();
  return route.name === name;
};

const availableLinks = [
  {
    to: { name: "search", symbol: search },
    label: "Search",
    for: ["cs", "csadm", "dev", "security"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "cspromotions", symbol: code },
    label: "Codes",
    for: ["cs", "csadm"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "promotions", symbol: code },
    label: "Promotions",
    for: ["dev"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "images", symbol: image },
    label: "Images",
    for: ["image", "dev"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "frames", symbol: frame },
    label: "Frames",
    for: ["image", "dev"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "categories", symbol: list },
    label: "Categories",
    for: ["image", "dev"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "broadcast", symbol: globe },
    label: "Broadcast Messages",
    for: ["dev"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "newsletter", symbol: envelope },
    label: "Newsletter",
    for: ["cs", "csadm", "dev", "newsletter"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "timeline", symbol: contacts },
    label: "Legacy search",
    for: ["cs", "csadm", "dev"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "stralfors", symbol: search },
    label: "Search",
    for: ["stralfors"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "systemlog", symbol: pn_document },
    label: "System log",
    for: ["dev", "csadm"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "accounts", symbol: sliders },
    label: "Access",
    for: ["dev"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "csaccounts", symbol: sliders },
    label: "Access",
    for: ["csadm"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "remoteConfig", symbol: sliders },
    label: "Remote Config",
    for: ["dev", "csadm"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "stickers", symbol: contacts },
    label: "Stickers",
    for: ["cs", "csadm", "dev", "image"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "mailout", symbol: envelope },
    label: "Orders",
    for: ["cs", "dev", "csadm"],
    app: constants.DASHBOARD_APPS.CREATE_MAIL_OUT,
  },
  {
    to: { name: "priceManagement", symbol: envelope },
    label: "Price Management",
    for: ["dev", "csadm"],
    app: constants.DASHBOARD_APPS.CREATE_MAIL_OUT,
  },
  {
    to: { name: "tagManagement", symbol: list },
    label: "Tag management",
    for: ["cs", "csadm", "dev"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
  {
    to: { name: "backsideTemplate", symbol: pn_document },
    label: "BackSide Template",
    for: ["dev"],
    app: constants.DASHBOARD_APPS.RIKTIGA_VYKORT,
  },
];

const links = computed(() => {
  return availableLinks.filter(
    (l) => l.for.includes(role.value) && l.app === selectedApp.value,
  );
});
</script>

<template>
  <nav>
    <div id="sidemenu-content">
      <div
        class="pn-side-menu-items-container"
        style="height: calc(-152px + 100vh)"
      >
        <div
          v-for="link in links"
          :key="link.label"
          :class="[
            'menu-level',
            { 'has-active-child': isActive(link.to.name) },
          ]"
        >
          <RouterLink
            :to="link.to"
            :class="[
              'menu-item',
              'level-0',
              { 'menu-item-active': isActive(link.to.name) },
            ]"
          >
            <pn-icon
              :symbol="link.to.symbol"
              :color="isActive(link.to.name) ? 'white' : 'black'"
            />
            <span class="menu-item-text">
              {{ link.label }}
            </span>
          </RouterLink>
        </div>
      </div>
    </div>
    <div id="sidemenu-placeholder"></div>
  </nav>
</template>

<style lang="scss" scoped>
@import "pn-design-assets/pn-assets/styles/pn-colors.scss";
a,
a:hover,
a:focus {
  text-decoration: none;
}
div {
  box-sizing: border-box;
}
nav {
  position: relative;
}
#sidemenu-content {
  border-right: 1px solid #d3cecb;
  position: absolute;
  top: 0;
  bottom: 0;
  padding-top: 2em;
  min-height: calc(100vh - 56px);
}

.menu-type-switch-container {
  z-index: 2;
  padding: 0 1em 0 1em;
  .menu-type-switch {
    position: fixed;
    bottom: 2%;
    width: 13em;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    background-color: white;
    min-height: 3em;
    border-radius: 3em;
    color: #005d92;
    border: 1px solid #005d92;
  }
}

.menu-level {
  position: relative;
  overflow: hidden;
  .has-active-child {
    background-color: #e0f8ff;
  }
}
.pn-side-menu-items-container {
  padding-bottom: 4.5em;
  overflow-x: hidden;
  overflow-y: auto;
}

#sidemenu-content,
#sidemenu-placeholder {
  width: 15em;
}

.menu-item {
  cursor: pointer;
  color: #5e554a;
  font-weight: normal;
  position: relative;
  display: flex;
  align-items: center;
  padding: 0.75em 0 0.75em 1em;
  padding-left: 1em;
  border-left: 0.35em solid transparent;
  > pn-icon {
    margin: 0 1em 0 -0.75em;
    display: inline-block;
  }
  &.level-0 {
    padding-left: 1.75em;
  }
  &:hover {
    border-left: 0.35em solid #005d92;
    background-color: #e0f8ff;
    color: #005d92;
  }
  &.menu-item-active {
    border-left: 0.35em solid #0d234b;
    background-color: #005d92;
    > .menu-item-text {
      color: #fff;
    }
  }
  span {
    font-size: 0.9em;
    line-height: normal;
  }
}

.menu-item-text {
  max-inline-size: 18ch;
}
</style>
